import { toast } from "react-toastify"
import * as yup from "yup"
import { useAppDispatch } from "~/ui-rtk/app/hooks"
import { useAuthControllerLogoutMutation } from "~/ui-rtk/api/authApi"
import {
  useCompanyControllerGetFinancialsQuery,
  useCompanyControllerUpsertFinancialsMutation,
} from "~/ui-rtk/api/companyApi"
import { emptySplitApi } from "~/ui-rtk/api/emptyApi"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"
import {
  WEEK_START_DAYS,
  resolveWeekStartDay,
  type WeekStartDay,
} from "~/shared/kpi"
import {
  DAILY_BUDGET_EVAL_PERIODS,
  DEFAULT_DAILY_BUDGET_EVAL_PERIOD,
} from "~/ui-rtk/constants/daily-budget-eval"
import {
  BRAND_SEARCH_POLICIES,
  DEFAULT_BRAND_SEARCH_POLICY,
  MAX_BUDGET_STEP_PCT_MAX,
  MAX_BUDGET_STEP_PCT_MIN,
  type BrandSearchPolicy,
} from "~/shared/budget-recommender/types"
import {
  TARGET_MER_DISPLAYS,
  merTypeForDisplay,
  resolveTargetMerDisplay,
  type TargetMerDisplay,
  type TargetMerScope,
  type TargetMerType,
} from "~/ui-rtk/utils/spend-guardrail"

export type ReturnRateMode = "computed" | "default" | "max"

export type TFinancialSettingsFormValues = {
  targetBrandSpendPercentOfTotal: number
  blendedGrossMargin: number
  spendIntoMarginalLtv: string
  shippingCostPercentAssm: number
  ccFeePercentAssm: number
  targetDRRoas: number
  targetBrandRoas: number
  targetDailyBudget: number
  targetDRRoasLocked: boolean
  targetBrandRoasLocked: boolean
  targetDailyBudgetLocked: boolean
  // Operative spend guardrail: the type + ratio every consumer reads.
  targetMerType: TargetMerType
  targetMerValue: number
  targetMerScope: TargetMerScope
  // How the customer expresses it (aMER, MER, or CAC). "cac" is an aMER seen
  // per customer, so targetMerType is kept at "amer" whenever this is "cac".
  targetMerDisplay: TargetMerDisplay
  dailyBudgetEvalPeriodDays: number
  manualChangeHoldDays: number
  // Budget Recommender (APP 539): how BRANDED_SEARCH rows are treated, and the
  // most one run may move a checked row (fraction of its current budget; null = off).
  brandSearchPolicy: BrandSearchPolicy
  maxBudgetStepPct: number | null
  // Amazon Financial Settings
  amazonDiscountPercent: number
  amazonRefundRate: number
  amazonShippingCostPercent: number
  amazonCogsPercent: number
  amazonCcFeePercent: number
  amazonAdSpendPercent: number
  // DTC Retail Financial Settings
  dtcRetailStaffingCostPercent: number
  dtcRetailCcFeePercent: number
  dtcRetailLocalMarketingPercent: number
  dtcRetailOtherVariableCostPercent: number
  // Return rate + product cost overrides (DTC/Shopify)
  defaultReturnRate: number
  returnRateMode: ReturnRateMode
  productCostMarkupPercent: number
  // Reporting
  weekStartDay: WeekStartDay
}

// Default values as percentages (0-1 scale)
export const DEFAULT_VALUES: TFinancialSettingsFormValues = {
  targetBrandSpendPercentOfTotal: 0.3, // 30%
  blendedGrossMargin: 0.7, // 70%
  spendIntoMarginalLtv: "first_order",
  shippingCostPercentAssm: 0.15, // 15%
  ccFeePercentAssm: 0.03, // 3%
  targetDRRoas: 0,
  targetBrandRoas: 0,
  targetDailyBudget: 0,
  targetDRRoasLocked: false,
  targetBrandRoasLocked: false,
  targetDailyBudgetLocked: false,
  targetMerType: "amer",
  targetMerValue: 0,
  targetMerScope: "total",
  targetMerDisplay: "amer",
  dailyBudgetEvalPeriodDays: DEFAULT_DAILY_BUDGET_EVAL_PERIOD,
  manualChangeHoldDays: 7,
  brandSearchPolicy: DEFAULT_BRAND_SEARCH_POLICY,
  maxBudgetStepPct: null,
  // Amazon Financial Settings
  amazonDiscountPercent: 0.05, // 5%
  amazonRefundRate: 0.05, // 5%
  amazonShippingCostPercent: 0.15, // 15%
  amazonCogsPercent: 0.65, // 65%
  amazonCcFeePercent: 0.15, // 15%
  amazonAdSpendPercent: 0.05, // 5%
  // DTC Retail Financial Settings
  dtcRetailStaffingCostPercent: 0.2, // 20%
  dtcRetailCcFeePercent: 0.035, // 3.5%
  dtcRetailLocalMarketingPercent: 0.1, // 10%
  dtcRetailOtherVariableCostPercent: 0, // 0%
  // Return rate + product cost overrides (DTC/Shopify)
  defaultReturnRate: 0, // 0%
  returnRateMode: "computed", // preserve existing dbt-computed behavior
  productCostMarkupPercent: 0, // 0%
  // Reporting
  weekStartDay: "monday", // Cube's default ISO weeks — no behavior change
}

export const validationSchema = yup.object().shape({
  targetBrandSpendPercentOfTotal: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  blendedGrossMargin: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  spendIntoMarginalLtv: yup
    .string()
    .oneOf(["first_order", "90d", "180d", "365d"]),
  shippingCostPercentAssm: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  ccFeePercentAssm: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  targetDRRoas: yup.number().min(0, "Must be at least 0").optional(),
  targetBrandRoas: yup.number().min(0, "Must be at least 0").optional(),
  targetDailyBudget: yup.number().min(0, "Must be at least 0").optional(),
  targetMerType: yup.string().oneOf(["amer", "mer"]).optional(),
  targetMerValue: yup.number().min(0, "Must be at least 0").optional(),
  targetMerScope: yup.string().oneOf(["ecomm", "total"]).optional(),
  targetMerDisplay: yup
    .string()
    .oneOf([...TARGET_MER_DISPLAYS])
    .optional(),
  dailyBudgetEvalPeriodDays: yup
    .number()
    .oneOf([...DAILY_BUDGET_EVAL_PERIODS])
    .optional(),
  manualChangeHoldDays: yup
    .number()
    .integer("Must be a whole number of days")
    .min(0, "Must be at least 0")
    .max(90, "Must be at most 90")
    .optional(),
  brandSearchPolicy: yup
    .string()
    .oneOf([...BRAND_SEARCH_POLICIES])
    .optional(),
  maxBudgetStepPct: yup
    .number()
    .nullable()
    .min(MAX_BUDGET_STEP_PCT_MIN, "Must be at least 10%")
    .max(MAX_BUDGET_STEP_PCT_MAX, "Must be at most 30%")
    .optional(),
  // Amazon Financial Settings
  amazonDiscountPercent: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  amazonRefundRate: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  amazonShippingCostPercent: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  amazonCogsPercent: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  amazonCcFeePercent: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  amazonAdSpendPercent: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  // DTC Retail Financial Settings
  dtcRetailStaffingCostPercent: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  dtcRetailCcFeePercent: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  dtcRetailLocalMarketingPercent: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  dtcRetailOtherVariableCostPercent: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  // Return rate + product cost overrides (DTC/Shopify)
  defaultReturnRate: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  returnRateMode: yup
    .string()
    .oneOf(["computed", "default", "max"])
    .required("Required"),
  productCostMarkupPercent: yup
    .number()
    .min(0, "Must be at least 0%")
    .max(1, "Must be at most 100%")
    .required("Required"),
  weekStartDay: yup
    .string()
    .oneOf([...WEEK_START_DAYS])
    .required("Required"),
})

export default function useConnect() {
  const currentCompany = useCurrentCompany()
  const dispatch = useAppDispatch()
  const { data: financials, isLoading: isFetchingFinancials } =
    useCompanyControllerGetFinancialsQuery(
      { id: currentCompany?.id },
      { skip: !currentCompany?.id },
    )
  const [upsertFinancials, { isLoading }] =
    useCompanyControllerUpsertFinancialsMutation()
  const [logout] = useAuthControllerLogoutMutation()
  const navigate = useStableNavigate()

  const initialValues: TFinancialSettingsFormValues = {
    targetBrandSpendPercentOfTotal:
      financials?.targetBrandSpendPercentOfTotal ??
      DEFAULT_VALUES.targetBrandSpendPercentOfTotal,
    blendedGrossMargin:
      financials?.blendedGrossMargin ?? DEFAULT_VALUES.blendedGrossMargin,
    spendIntoMarginalLtv:
      financials?.spendIntoMarginalLtv ?? DEFAULT_VALUES.spendIntoMarginalLtv,
    shippingCostPercentAssm:
      financials?.shippingCostPercentAssm ??
      DEFAULT_VALUES.shippingCostPercentAssm,
    ccFeePercentAssm:
      financials?.ccFeePercentAssm ?? DEFAULT_VALUES.ccFeePercentAssm,
    targetDRRoas: financials?.targetDRRoas ?? DEFAULT_VALUES.targetDRRoas,
    targetBrandRoas:
      financials?.targetBrandRoas ?? DEFAULT_VALUES.targetBrandRoas,
    targetDailyBudget:
      financials?.targetDailyBudget ?? DEFAULT_VALUES.targetDailyBudget,
    targetDRRoasLocked:
      financials?.targetDRRoasLocked ?? DEFAULT_VALUES.targetDRRoasLocked,
    targetBrandRoasLocked:
      financials?.targetBrandRoasLocked ?? DEFAULT_VALUES.targetBrandRoasLocked,
    targetDailyBudgetLocked:
      financials?.targetDailyBudgetLocked ??
      DEFAULT_VALUES.targetDailyBudgetLocked,
    targetMerType:
      (financials?.targetMerType as TargetMerType) ??
      DEFAULT_VALUES.targetMerType,
    targetMerValue: financials?.targetMerValue ?? DEFAULT_VALUES.targetMerValue,
    targetMerScope:
      (financials?.targetMerScope as TargetMerScope) ??
      DEFAULT_VALUES.targetMerScope,
    targetMerDisplay: resolveTargetMerDisplay(financials),
    dailyBudgetEvalPeriodDays:
      financials?.dailyBudgetEvalPeriodDays ??
      DEFAULT_VALUES.dailyBudgetEvalPeriodDays,
    manualChangeHoldDays:
      financials?.manualChangeHoldDays ?? DEFAULT_VALUES.manualChangeHoldDays,
    brandSearchPolicy:
      financials?.brandSearchPolicy ?? DEFAULT_VALUES.brandSearchPolicy,
    maxBudgetStepPct:
      financials?.maxBudgetStepPct ?? DEFAULT_VALUES.maxBudgetStepPct,
    amazonDiscountPercent:
      financials?.amazonDiscountPercent ?? DEFAULT_VALUES.amazonDiscountPercent,
    amazonRefundRate:
      financials?.amazonRefundRate ?? DEFAULT_VALUES.amazonRefundRate,
    amazonShippingCostPercent:
      financials?.amazonShippingCostPercent ??
      DEFAULT_VALUES.amazonShippingCostPercent,
    amazonCogsPercent:
      financials?.amazonCogsPercent ?? DEFAULT_VALUES.amazonCogsPercent,
    amazonCcFeePercent:
      financials?.amazonCcFeePercent ?? DEFAULT_VALUES.amazonCcFeePercent,
    amazonAdSpendPercent:
      financials?.amazonAdSpendPercent ?? DEFAULT_VALUES.amazonAdSpendPercent,
    dtcRetailStaffingCostPercent:
      financials?.dtcRetailStaffingCostPercent ??
      DEFAULT_VALUES.dtcRetailStaffingCostPercent,
    dtcRetailCcFeePercent:
      financials?.dtcRetailCcFeePercent ?? DEFAULT_VALUES.dtcRetailCcFeePercent,
    dtcRetailLocalMarketingPercent:
      financials?.dtcRetailLocalMarketingPercent ??
      DEFAULT_VALUES.dtcRetailLocalMarketingPercent,
    dtcRetailOtherVariableCostPercent:
      financials?.dtcRetailOtherVariableCostPercent ??
      DEFAULT_VALUES.dtcRetailOtherVariableCostPercent,
    defaultReturnRate:
      financials?.defaultReturnRate ?? DEFAULT_VALUES.defaultReturnRate,
    returnRateMode:
      (financials?.returnRateMode as ReturnRateMode) ??
      DEFAULT_VALUES.returnRateMode,
    productCostMarkupPercent:
      financials?.productCostMarkupPercent ??
      DEFAULT_VALUES.productCostMarkupPercent,
    weekStartDay: resolveWeekStartDay(financials),
  }

  const updateFinancialSettings = async (
    values: TFinancialSettingsFormValues,
  ) => {
    if (!currentCompany?.id) {
      return
    }

    const weekStartDayChanged =
      values.weekStartDay !== resolveWeekStartDay(financials)

    try {
      await upsertFinancials({
        id: currentCompany.id,
        body: {
          targetBrandSpendPercentOfTotal: values.targetBrandSpendPercentOfTotal,
          blendedGrossMargin: values.blendedGrossMargin,
          spendIntoMarginalLtv: values.spendIntoMarginalLtv as
            | "first_order"
            | "90d"
            | "180d"
            | "365d"
            | undefined,
          shippingCostPercentAssm: values.shippingCostPercentAssm,
          ccFeePercentAssm: values.ccFeePercentAssm,
          targetDRRoas: values.targetDRRoas,
          targetBrandRoas: values.targetBrandRoas,
          targetDailyBudget: values.targetDailyBudget,
          targetDRRoasLocked: values.targetDRRoasLocked,
          targetBrandRoasLocked: values.targetBrandRoasLocked,
          targetDailyBudgetLocked: values.targetDailyBudgetLocked,
          // A CAC display is stored against the aMER operative type.
          targetMerType: merTypeForDisplay(values.targetMerDisplay),
          targetMerValue: values.targetMerValue,
          targetMerScope: values.targetMerScope,
          targetMerDisplay: values.targetMerDisplay,
          dailyBudgetEvalPeriodDays: values.dailyBudgetEvalPeriodDays,
          manualChangeHoldDays: values.manualChangeHoldDays,
          brandSearchPolicy: values.brandSearchPolicy,
          maxBudgetStepPct: values.maxBudgetStepPct,
          amazonDiscountPercent: values.amazonDiscountPercent,
          amazonRefundRate: values.amazonRefundRate,
          amazonShippingCostPercent: values.amazonShippingCostPercent,
          amazonCogsPercent: values.amazonCogsPercent,
          amazonCcFeePercent: values.amazonCcFeePercent,
          amazonAdSpendPercent: values.amazonAdSpendPercent,
          dtcRetailStaffingCostPercent: values.dtcRetailStaffingCostPercent,
          dtcRetailCcFeePercent: values.dtcRetailCcFeePercent,
          dtcRetailLocalMarketingPercent: values.dtcRetailLocalMarketingPercent,
          dtcRetailOtherVariableCostPercent:
            values.dtcRetailOtherVariableCostPercent,
          defaultReturnRate: values.defaultReturnRate,
          returnRateMode: values.returnRateMode,
          productCostMarkupPercent: values.productCostMarkupPercent,
          weekStartDay: values.weekStartDay,
        },
      }).unwrap()
      // The cube token carries a weekStartDay claim that drives weekly
      // bucketing server-side; it is minted with the session. Refetch the
      // session so a changed week start applies to cube queries immediately
      // instead of waiting out the current token's TTL.
      if (weekStartDayChanged) {
        dispatch(emptySplitApi.util.invalidateTags(["Auth"]))
      }
      toast.success("Financial settings saved successfully")
    } catch (error: unknown) {
      console.error(error)
      const status =
        typeof error === "object" && error !== null && "status" in error
          ? (error as { status?: number }).status
          : undefined
      if (status === 401) {
        await logout()
        navigate("/")
        return
      }
      toast.error("Could not save financial settings")
    }
  }

  return {
    initialValues,
    validationSchema,
    updateFinancialSettings,
    isLoading,
    isFetchingFinancials,
  }
}
